import type { PersonLinkPerson } from "@/components/common/PersonLink";

// Die Aufteilung eines Projekts: Pakete mit Zeitraum, darin Aufgaben mit
// Zuweisung, und daneben Termine.
//
// Der Unterschied zwischen den beiden Sorten ist nicht Größe, sondern Form:
// Paket und Aufgabe DAUERN (zwei Daten, ein Balken), ein Termin GESCHIEHT (ein
// Datum, ein Punkt). Deshalb ist ein Termin kein Paket ohne Länge, sondern ein
// eigener Typ - sonst müsste jede Anzeige raten, ob ein Balken der Breite null
// ein Fehler ist oder eine Absicht.
//
// Termine hängen am Projekt, nicht an einem Paket: eine Abgabefrist oder ein
// Kick-off gehört selten zu genau einem Arbeitspaket, und im Personfokus gilt so
// ein Datum ohnehin für alle Spuren zugleich. Kein Fixture mehr, sondern das, was der Mensch eingibt, und genau
// so liegt es auch in genesis unter `data.packages` (ProjectData.Package).
//
// Titel sind schlichter Text. Vorher trugen sie i18n-Objekte, weil sie aus
// übersetzten Beispieldaten kamen; ein eingetippter Paketname ist aber kein
// Katalogeintrag, und das Umbenennen musste sich deshalb eine erfundene
// Message-Id ausdenken, die nie im Katalog stehen durfte. Mit Text fällt der
// ganze Umweg weg.
//
// Die Datumsangaben bleiben ISO-Tage (yyyy-mm-dd): die Swimlane rechnet
// geometrisch damit, eine Wortangabe ("ca. 4 Wochen") kann man nicht platzieren.

export interface ProjectTask {
  id: string;
  title: string;
  /** Person-Id aus genesis; null = noch offen. Lose Id ohne Fremdschlüssel, wie in ProjectData. */
  assigneeId: string | null;
  start: string;
  end: string;
}

export interface ProjectPackage {
  id: string;
  name: string;
  /**
   * Wer das Paket verantwortet; null = niemand. Dieselbe lose Person-Id wie bei
   * einer Aufgabe: ein Paket ist ein Stück Arbeit, und Arbeit hat einen
   * Zuständigen, auch wenn sie noch in Aufgaben zerfällt.
   */
  assigneeId: string | null;
  start: string;
  end: string;
  tasks: readonly ProjectTask[];
}

/**
 * Meilenstein, Frist oder Termin. Die Art färbt nichts ein - rot ist in spark
 * für Zerstörerisches reserviert (UI_CONVENTIONS) -, sie steht als Wort daneben
 * und entscheidet die Form des Punktes.
 */
export type ProjectEventKind = "milestone" | "deadline" | "meeting";

export const PROJECT_EVENT_KINDS: readonly ProjectEventKind[] = ["milestone", "deadline", "meeting"];

/** Beschriftung je Art. Liegt bei den Typen, nicht in einer Komponente - drei Dateien lesen sie. */
export const EVENT_KIND_LABELS: Record<ProjectEventKind, { id: string; defaultMessage: string }> = {
  milestone: { id: "project.event.kind.milestone", defaultMessage: "Meilenstein" },
  deadline: { id: "project.event.kind.deadline", defaultMessage: "Frist" },
  meeting: { id: "project.event.kind.meeting", defaultMessage: "Termin" },
};

export interface ProjectEvent {
  id: string;
  title: string;
  /** Der Tag, an dem es geschieht. Ein Punkt, kein Zeitraum. */
  date: string;
  kind: ProjectEventKind;
}

/** Eine Person, die in Spuren und Listen auftaucht. Passt auf den PersonLink-Baustein. */
export interface PlanPerson extends PersonLinkPerson {
  id: string;
}

/**
 * Der Lebenszustand eines Projekts. Gedacht in Phasen eines Vorhabens, nicht in
 * Bearbeitungszuständen einer Akte: ein Projekt beginnt als Idee, sucht Mittel,
 * läuft, wächst - und endet auf eine von zwei Arten. Die Reihenfolge hier ist
 * die Reihenfolge im Auswahlfeld und gleichzeitig die übliche Abfolge.
 *
 * Bewusst KEIN Automatismus: der Status ist eine Aussage des Menschen über sein
 * Vorhaben. Vorher stand dort "Entwurf" oder "In Arbeit", abgeleitet aus der
 * Anzahl gefüllter Flächen - das war eine Aussage über die Maske, nicht über
 * das Projekt.
 */
export type ProjectStatus =
  | "IDEA"
  | "SEED"
  | "FUNDING"
  | "ACTIVE"
  | "SCALING"
  | "PAUSED"
  | "DONE"
  | "CANCELLED";

export const PROJECT_STATUSES: readonly ProjectStatus[] = [
  "IDEA",
  "SEED",
  "FUNDING",
  "ACTIVE",
  "SCALING",
  "PAUSED",
  "DONE",
  "CANCELLED",
];

/** Beschriftung je Status. Liegt bei den Typen, weil mehrere Dateien sie lesen. */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, { id: string; defaultMessage: string }> = {
  IDEA: { id: "project.status.idea", defaultMessage: "Idee" },
  SEED: { id: "project.status.seed", defaultMessage: "Seed" },
  FUNDING: { id: "project.status.funding", defaultMessage: "Finanzierung" },
  ACTIVE: { id: "project.status.active", defaultMessage: "In Umsetzung" },
  SCALING: { id: "project.status.scaling", defaultMessage: "Skalierung" },
  PAUSED: { id: "project.status.paused", defaultMessage: "Pausiert" },
  DONE: { id: "project.status.done", defaultMessage: "Abgeschlossen" },
  CANCELLED: { id: "project.status.cancelled", defaultMessage: "Abgebrochen" },
};

/**
 * Liest `data.status`. Alles Unbekannte wird zur Idee - auch das alte "DRAFT"
 * aus der Zeit, als der Status noch abgeleitet wurde. Ein Projekt ohne
 * erkennbaren Status ist am ehesten eins, das gerade erst anfängt.
 */
export function parseStatus(raw: unknown): ProjectStatus {
  return typeof raw === "string" && (PROJECT_STATUSES as readonly string[]).includes(raw)
    ? (raw as ProjectStatus)
    : "IDEA";
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function asIsoDay(value: unknown): string {
  return typeof value === "string" && ISO_DAY.test(value) ? value : "";
}

function parseTask(raw: unknown): ProjectTask[] {
  if (typeof raw !== "object" || raw === null) return [];
  const o = raw as Record<string, unknown>;
  if (typeof o.id !== "string") return [];
  return [
    {
      id: o.id,
      title: typeof o.title === "string" ? o.title : "",
      assigneeId: typeof o.assigneeId === "string" ? o.assigneeId : null,
      start: asIsoDay(o.start),
      end: asIsoDay(o.end),
    },
  ];
}

/**
 * Liest `data.packages` aus dem Object-Skalar. Alles, was nicht die erwartete
 * Form hat, fällt raus statt die Seite mitzureißen: das Dokument ist loses
 * JSONB, in dem auch ein älterer Stand oder ein Handeingriff liegen kann.
 */
export function parsePackages(raw: unknown): ProjectPackage[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const o = entry as Record<string, unknown>;
    if (typeof o.id !== "string") return [];
    return [
      {
        id: o.id,
        name: typeof o.name === "string" ? o.name : "",
        assigneeId: typeof o.assigneeId === "string" ? o.assigneeId : null,
        start: asIsoDay(o.start),
        end: asIsoDay(o.end),
        tasks: Array.isArray(o.tasks) ? o.tasks.flatMap(parseTask) : [],
      },
    ];
  });
}

/**
 * Das Anforderungsprofil: Fachkompetenzen auf der 7er-Leiter plus die Gewichtung der
 * Powers. Genau die Sprache, in der auch ein Match rechnet - was hier entsteht,
 * ist die Grundlage einer späteren Suche und keine Projekt-Privatnotation.
 *
 * Die Form spiegelt `ProjectData.Requirements` in genesis. Anders als dort trägt
 * ein Eintrag hier eine `id`: die Oberfläche muss ihn beim Bearbeiten und
 * Löschen wiedererkennen, und der Text taugt dafür nicht, weil er sich ändert.
 */
export interface ProjectExpertiseRequirement {
  id: string;
  description: string;
  /** 1 bis 7 auf der Fachkompetenz-Leiter; 0 = vorhanden ohne Mindeststufe. */
  level: number;
  /** MUST oder OPTIONAL. */
  requirement: "MUST" | "OPTIONAL";
  /** Wie viele Menschen das mindestens können müssen. */
  personCount?: number;
}

export interface ProjectRequirements {
  expertise: readonly ProjectExpertiseRequirement[];
  /** CreativityElement-Schlüssel auf Gewicht 0..1000, wie im Match. */
  elementWeights: Record<string, number>;
}

export const EMPTY_REQUIREMENTS: ProjectRequirements = { expertise: [], elementWeights: {} };

export function parseRequirements(raw: unknown): ProjectRequirements {
  if (typeof raw !== "object" || raw === null) return EMPTY_REQUIREMENTS;
  const o = raw as Record<string, unknown>;
  const expertise = Array.isArray(o.expertise)
    ? o.expertise.flatMap((entry): ProjectExpertiseRequirement[] => {
        if (typeof entry !== "object" || entry === null) return [];
        const e = entry as Record<string, unknown>;
        if (typeof e.id !== "string") return [];
        return [
          {
            id: e.id,
            description: typeof e.description === "string" ? e.description : "",
            level: typeof e.level === "number" ? e.level : 0,
            requirement: e.requirement === "OPTIONAL" ? "OPTIONAL" : "MUST",
            ...(typeof e.personCount === "number" ? { personCount: e.personCount } : {}),
          },
        ];
      })
    : [];
  const weights: Record<string, number> = {};
  if (typeof o.elementWeights === "object" && o.elementWeights !== null) {
    for (const [key, value] of Object.entries(o.elementWeights as Record<string, unknown>)) {
      if (typeof value === "number") weights[key] = value;
    }
  }
  return { expertise, elementWeights: weights };
}

function isEventKind(value: unknown): value is ProjectEventKind {
  return typeof value === "string" && (PROJECT_EVENT_KINDS as readonly string[]).includes(value);
}

/** Liest `data.events`. Gleiche Vorsicht wie bei den Paketen: Unbrauchbares fällt raus. */
export function parseEvents(raw: unknown): ProjectEvent[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const o = entry as Record<string, unknown>;
    if (typeof o.id !== "string") return [];
    return [
      {
        id: o.id,
        title: typeof o.title === "string" ? o.title : "",
        date: asIsoDay(o.date),
        // Eine unbekannte Art wird zum Meilenstein, statt den Eintrag zu verwerfen:
        // das Datum ist die Information, die Art nur ihre Betonung.
        kind: isEventKind(o.kind) ? o.kind : "milestone",
      },
    ];
  });
}

/**
 * Die Menschen, die man für das Projekt im Auge hat. Noch keine Mitglieder mit
 * Zugriff, sondern Gefragte - deshalb eine eigene Liste und nicht `project_access`.
 * Zuweisbar sind sie trotzdem: wer eine Aufgabe übernehmen soll, muss vorher
 * gefragt worden sein, nicht schon zugesagt haben.
 */
export function parseCandidates(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((id): id is string => typeof id === "string");
}

/** Die zugewiesenen Personen-Ids über alle Pakete, ohne Dubletten. */
export function assignedPersonIds(packages: readonly ProjectPackage[]): string[] {
  const ids = new Set<string>();
  for (const pkg of packages) {
    // Auch die Zuweisung des PAKETS zählt. Sie fehlte hier, und damit bekam
    // jemand, dem ein ganzes Paket gehört, aber keine einzelne Aufgabe, keine
    // Spur im Personfokus - man wies zu und sah nichts.
    if (pkg.assigneeId) ids.add(pkg.assigneeId);
    for (const task of pkg.tasks) {
      if (task.assigneeId) ids.add(task.assigneeId);
    }
  }
  return [...ids];
}
